import React, {useEffect, useState} from 'react';

import {useForm} from 'react-hook-form';
import {Events} from 'src/analytics';
import {SubmittedOverlay} from 'src/components/apply/SubmittedOverlay';
import {Box, Spinner} from 'src/components/shared';
import {Typography} from 'src/components/shared/fireworkTypography';
import {useUTMParams} from 'src/hooks/useUTMParams';
import {BasicTheme} from 'src/utils/makeTailwindBasicThemeClasses';
import styled from 'styled-components/macro';

import {ErrorTextBlurb} from './formUtils/errorTextBlurb';
import {getValidateFunction} from './formUtils/fieldValidators';
import {getLeadFormSubmissionHandler} from './formUtils/onLeadFormSubmit';
import {LeadFormFieldDescriptor, LeadFormFieldWithLabel} from './LeadFormFieldWithLabel';

const {H3} = Typography;

const DEFAULT_SUBMIT_BUTTON_TEXT = 'Submit';
const DEFAULT_SUCCESSFUL_SUBMIT_TEXT = 'Submission received.';
const SUBMISSION_ERROR_TEXT = 'Something went wrong. Please try again later or contact support.';

// Subset of RawLeadFormDataType
export type LeadFormProps = {
  fields?: LeadFormFieldDescriptor[];
  customSubmitButtonText?: string;
  customSuccessMessage?: string;
  eventData?: any;
  theme?: BasicTheme;
};

const Wrapper: typeof Box = styled(Box).attrs({
  width: '100%',
  pt: '4.5rem',
})``;

const Container: typeof Box = styled(Box).attrs({
  textAlign: 'center',
})``;

const StyledH3: typeof H3 = styled(H3).attrs({
  fontSize: '1.5rem',
  lineHeight: '1.75rem',
})``;

const StyledSpinner: typeof Spinner = styled(Spinner).attrs({
  mx: 'auto',
})`
  margin: 1.5rem auto 0;

  &:before {
    border-color: ${({theme}): string => theme.colors.neonGreen};
    border-width: '5px';
    border-top-color: ${({theme}): string => theme.colors.darkGreen};
  }
`;

const SubmittingScreen: React.FC = () => {
  window.scrollTo(0, 0);

  return (
    <Wrapper>
      <Container>
        <StyledH3>Please wait while your application is being submitted</StyledH3>

        <StyledSpinner />
      </Container>
    </Wrapper>
  );
};

/**
 * A LeadForm! Sometimes embedded into a site builder page inside a "Lead Form Section",
 * and sometimes rendered into a standalone page for embedding in an iframe.
 */
const LeadForm: React.FC<LeadFormProps> = ({
  fields,
  customSubmitButtonText,
  customSuccessMessage,
  eventData,
  theme,
}) => {
  const {register, handleSubmit, formState} = useForm();
  const [showForm, setShowForm] = useState(true);
  const [submittedSuccessfully, setSubmittedSuccessfully] = useState(false);
  const [hasSubmissionError, setHasSubmissionError] = useState(false);
  const utmParams = useUTMParams();

  const onLeadFormSubmit = getLeadFormSubmissionHandler(
    setShowForm,
    setSubmittedSuccessfully,
    setHasSubmissionError,
    utmParams,
  );

  customSuccessMessage = customSuccessMessage?.length ? customSuccessMessage : DEFAULT_SUCCESSFUL_SUBMIT_TEXT;

  useEffect(() => {
    if (!submittedSuccessfully || !eventData) {
      return;
    }

    Events.didSubmitForm(eventData);
  }, [submittedSuccessfully]);

  const buttonStyles =
    theme === 'darkblue'
      ? 'bg-green-brat hover:bg-transparent hover:text-green-brat text-green-dark rounded-full border-2 border-color-green-brat'
      : 'bg-dark-gold hover:bg-light-gold text-white';

  return (
    <form className="lead-form" noValidate onSubmit={handleSubmit(onLeadFormSubmit)}>
      {showForm &&
        fields?.map(field => {
          const fieldId = `lead-form-field-${field._key}`;
          const salesforceFieldName = field.salesforceFieldName;
          const fieldError = formState.errors[salesforceFieldName];
          const errorMessage = fieldError?.message?.toString();
          const customValidator = getValidateFunction(field._type);

          return (
            <LeadFormFieldWithLabel
              key={fieldId}
              fieldId={fieldId}
              field={field}
              className="mb-4 w-full lead-form-field"
              errorMessage={errorMessage}
              isRequired={field.isRequired}
              theme={theme}
              inputElementProps={register(salesforceFieldName, {
                required: field.isRequired ? 'This field is required.' : undefined,
                validate: customValidator,
              })}
            />
          );
        })}

      {showForm && (
        <button
          type="submit"
          disabled={submittedSuccessfully}
          className={`${buttonStyles} text-sm font-bold p-2 px-12 flex`}
        >
          {customSubmitButtonText || DEFAULT_SUBMIT_BUTTON_TEXT}
        </button>
      )}

      {formState.isSubmitting && <SubmittingScreen />}

      {submittedSuccessfully && <SubmittedOverlay isOpen={true} customSuccessMessage={customSuccessMessage} />}

      {hasSubmissionError && !formState.isSubmitting && <ErrorTextBlurb errorMessage={SUBMISSION_ERROR_TEXT} />}
    </form>
  );
};

export {LeadForm};
